import React, { useMemo, useCallback } from "react";
import { Chip, Input, Box } from "@mantine/core";
import { useTranslation } from "react-i18next";

import {
  TRAVEL_TYPE_OPTIONS,
  TravelTypeValue,
} from "@/config/travelTypes";

import { AppIcon } from "@/components/generic/AppIcon";

// ============================================
// Types
// ============================================

export interface TravelTypeSelectorProps {
  /** Field name for form binding */
  name?: string;

  /** Currently selected travel type value */
  value?: TravelTypeValue | string | null | undefined;

  /** Callback fired when travel type selection changes: (name, value) */
  onChange?: (name: string, value: TravelTypeValue | null) => void;

  /** Whether the field is required */
  required?: boolean;

  /** Error message to display */
  error?: string | null | undefined;

  /** Whether the selector is disabled */
  disabled?: boolean;

  /** Size of the input wrapper (for label/error consistency) */
  size?: "xs" | "sm" | "md" | "lg" | "xl";

  /** Label for the field */
  label?: string;

  /** Chip variant: outline (default) or filled */
  variant?: "outline" | "filled" | "light";
}

// ============================================
// Styles
// ============================================

/**
 * Chip label styles to visually match Mantine md input height (36px).
 * Each chip stretches to fill its flex cell via width: 100%.
 */
const CHIP_LABEL_STYLES = {
  borderRadius: "var(--mantine-radius-md)",
  height: "42px",
  paddingLeft: "12px",
  paddingRight: "14px",
  fontSize: "var(--mantine-font-size-sm)",
  width: "100%",
  justifyContent: "center",
};

const CHIP_ICON_WRAPPER_STYLES = {
  width: "auto",
  height: '20px',
  paddingRight: "4px"
};

/**
 * Each chip root takes equal space in the flex row.
 * On small screens chips wrap and each takes ~half width for a balanced 3+2 or 2+3 layout.
 */
const CHIP_ROOT_STYLES = {
  flex: "1 1 0",
  minWidth: 0,
};

/** Flex container: full-width row that wraps on small screens */
const GROUP_STYLES: React.CSSProperties = {
  display: "flex",
  flexWrap: "wrap",
  gap: "var(--mantine-spacing-xs)",
};

// ============================================
// Component
// ============================================

const TravelTypeSelectorComponent: React.FC<TravelTypeSelectorProps> = ({
  name = "travelType",
  value,
  onChange,
  required = false,
  error,
  disabled,
  size = "md",
  label,
  variant = "outline",
}) => {
  const { t } = useTranslation(["travels", "common"]);

  const defaultLabel = label ?? t("travels:selectors.travelType.label");

  // Handle chip selection change
  const handleChange = useCallback(
    (selectedValue: string) => {
      if (selectedValue === value && !required) {
        onChange?.(name, null);
      } else {
        onChange?.(name, selectedValue as TravelTypeValue);
      }
    },
    [name, value, required, onChange]
  );

  // Build chip elements
  const chips = useMemo(() => {
    return TRAVEL_TYPE_OPTIONS.map((config) => {
      const translatedLabel = t(config.translationKey, {
        defaultValue: config.value,
      });

      return (
        <Chip
          key={config.value}
          value={config.value}
          color={config.color}
          variant={variant}
          size="sm"
          disabled={disabled}
          icon={<AppIcon iconName={config.icon} size={20} />}
          styles={{
            label: CHIP_LABEL_STYLES,
            iconWrapper: CHIP_ICON_WRAPPER_STYLES,
            root: CHIP_ROOT_STYLES,
          }}
        >
          {translatedLabel}
        </Chip>
      );
    });
  }, [t, variant, disabled]);

  return (
    <Input.Wrapper
      label={defaultLabel}
      required={required}
      error={error}
      size={size}
    >
      <Box mt={4} style={GROUP_STYLES}>
        <Chip.Group
          multiple={false}
          value={value ?? ""}
          onChange={handleChange}
        >
          {chips}
        </Chip.Group>
      </Box>
    </Input.Wrapper>
  );
};

export const TravelTypeSelector = React.memo(TravelTypeSelectorComponent);